import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

type PaginationControlsProps = {
  page: number;
  pageSize: number;
  currentCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  totalCount?: number;
  hasNextPage?: boolean;
  itemLabel?: string;
  className?: string;
};

export function PaginationControls({
  page,
  pageSize,
  currentCount,
  onPageChange,
  onPageSizeChange,
  totalCount,
  hasNextPage,
  itemLabel = "rows",
  className,
}: PaginationControlsProps) {
  const start = currentCount > 0 ? (page - 1) * pageSize + 1 : 0;
  const end = currentCount > 0 ? start + currentCount - 1 : 0;
  const totalPages =
    typeof totalCount === "number" ? Math.max(Math.ceil(totalCount / pageSize), 1) : undefined;
  const canGoPrev = page > 1;
  const canGoNext =
    typeof totalPages === "number"
      ? page < totalPages
      : Boolean(hasNextPage);

  return (
    <div
      className={cn(
        "mt-3 flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div>
        {start > 0 ? `Showing ${start}-${end}` : "Showing 0"}
        {typeof totalCount === "number" ? ` of ${totalCount}` : ""}
        {` ${itemLabel}`}
      </div>

      <div className="flex items-center gap-2">
        {onPageSizeChange && (
          <>
            <span>Rows per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                const parsed = Number(value);
                if (!Number.isFinite(parsed)) return;
                onPageSizeChange(parsed);
              }}
            >
              <SelectTrigger className="w-[84px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!canGoPrev}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!canGoNext}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
